import React, {useEffect, useState} from 'react';
import {connect, useDispatch} from "react-redux";
import {usersActions} from "../../modules/users/users.actions";
import Loader from "./layout/loading/loader";
import {Formik} from "formik";
import UserDataField from "./users/form/UserDataField";
import './UsersNewOrEdit.css'
import {array, boolean, object, string} from 'yup';
import {Container} from "@mui/material";
import {isExploitant, Role} from "../../utils/roles";
import {useParams, useNavigate, useLocation} from "react-router-dom";
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SubmitButton from "./layout/forms/SubmitButton";
import {PeopleAlt} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle";
import {helper} from "../../utils/helper";
import Constants from "../../utils/constants";
import {geoActions} from "../../modules/geo/geo.actions";
import {agencesActions} from "../../modules/agences/agences.actions";
import Grid from "@mui/material/Grid2";


const userValidationSchema = object({
    email: string()
        .email('L\'email doit être valide')
        .required('L\'email est requis'),
    password: string()
        .when('isCreating', {
            is: true,
            then: () => string().required('Le mot de passe est requis'),
            otherwise: () => string().nullable(),
        }),
    libelle: string()
        .nullable()
        .max(255, 'Le libellé ne doit pas dépasser 255 caractères'),
    rfid: string()
        .nullable()
        .max(255, 'Le rfid ne doit pas dépasser 255 caractères'),
    isAccredited: boolean().nullable(),
    exploitant: string()
        .when('roles', {
            is: (roles) => roles.includes(Role.ROLE_CLIENT) || roles.includes(Role.ROLE_OPERATEUR),
            then: () => string().required('L\'exploitant est requis'),
            otherwise: () => string().nullable(),
        }),
    serviceTypes: array()
        .when('roles', {
            is: (roles) => roles && roles.includes(Role.ROLE_CLIENT),
            then: () => array().min(1, 'Le type de service est requis').required('Le type de service est requis'),
            otherwise: () => array().nullable(),
        }),
    ville: string()
        .when(['roles','serviceTypes'], {
            is: (roles, serviceTypes) => Array.isArray(roles) && roles.includes(Role.ROLE_CLIENT) && Array.isArray(serviceTypes) && serviceTypes.includes('graffiti'),
            then: () => string().required('La ville est requise'),
            otherwise: () => string().nullable(),
        }),
});

function UsersNewOrEdit({...props}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const navigateBack = () => {
        const returnSearch = location.state?.returnSearch ?? '';
        navigate(Constants.APP_ROUTE_ADMIN_USERS + returnSearch);
    };
    const params = useParams();
    const [mode, setMode] = useState("create");
    const [userId, setUserId] = useState(null);

    useEffect(() => {
        if (params.hasOwnProperty("id")) {
            setMode("edit");
            setUserId(params.id);
        } else {
            setMode("create");
            setUserId(null);
        }
    }, [params]);

    useEffect(() => {
        if (userId && !props.user && !props.loading && !props.error) {
            let userType = params.type;
            dispatch(usersActions.fetchUser(userType, userId));
        }
        if (!props.exploitants && !props.loading && !props.error && mode === "create") {
            dispatch(usersActions.fetchExploitants())
        }
        // Charger la liste des villes (geo) si non chargée
        if (!props.villes && !props.geoLoading) {
            dispatch(geoActions.fetchVilles());
        }
        // Charger la liste des agences si non chargée
        if (!props.agences && !props.agencesLoading) {
            dispatch(agencesActions.fetchAgences());
        }
    }, [props, dispatch, params.type, userId]);

    useEffect(() => {
        return () => {
            dispatch(usersActions.clearUser());
        };
    }, [dispatch]);

    const handleLogoChanged = (event, form) => {
        const formData = new FormData();
        formData.append("file", event.target.files[0]);

        dispatch(usersActions.createLogo(formData)).then((logo) => {
            form.setFieldValue("logo", logo['@id'])
            form.setFieldValue("logoUrl", logo.contentUrl)
        }).catch((error) => {
            console.error(error)
        })
    }

    const handleSubmit = (values) => {
        if (values.logo?.hasOwnProperty("@id")) {
            values.logoUrl = values.logo?.contentUrl
            values.logo = values.logo["@id"];
        }
        // Champs IRI optionnels : une chaîne vide n'est pas une IRI valide pour API Platform (« Invalid IRI "" »)
        if (values.ville === "") delete values.ville
        if (values.agence === "") values.agence = null // null = « sans agence » (relation nullable), et permet de la retirer
        if (values.roles.includes(Role.ROLE_CLIENT)) {
            if (mode === "create") {
                dispatch(usersActions.createClient(values, navigateBack));
            } else {
                dispatch(usersActions.updateClient(values, values.id, navigateBack));
            }

        } else if (values.roles.includes(Role.ROLE_OPERATEUR)) {
            if (mode === "create") {
                dispatch(usersActions.createOperateur(values, navigateBack));
            } else {
                dispatch(usersActions.updateOperateur(values, values.id, navigateBack));
            }

        } else if (values.roles.includes(Role.ROLE_EXPLOITANT)) {
            if (values.roles.includes(Role.ROLE_EXPLOITANT)) {
                delete values.exploitant
            }
            if (mode === "create") {
                dispatch(usersActions.createExploitant(values, navigateBack));
            } else {
                dispatch(usersActions.updateExploitant(values, values.id, navigateBack));
            }
        } else if (values.roles.includes(Role.ROLE_ADMIN)) {
            if (values.roles.includes(Role.ROLE_EXPLOITANT)) {
                delete values.exploitant
            }
            if (mode === "create") {
                dispatch(usersActions.createUser(values, navigateBack));
            } else {
                dispatch(usersActions.updateUser(values, values.id, navigateBack));
            }
        }


    }

    const initialValues = {
        id: props.user?.id ?? null,
        libelle: props.user?.libelle ?? '',
        email: props.user?.email ?? '',
        password: '',
        logo: props.user?.logo,
        logoUrl: props.user?.logo?.contentUrl,
        roles: props.user?.roles ?? [Role.ROLE_EXPLOITANT],
        // Types de services pour les clients: ['lavage','graffiti']
        serviceTypes: props.user?.serviceTypes ?? [],
        // Ville IRI pour clients graffiti
        ville: props.user?.ville?.['@id'] ?? '',
        libelleFacturation: props.user?.libelleFacturation ?? '',
        agence: props.user?.agence ? `/api/agences/${props.user.agence.id}` : '',
        isAccredited: props.user?.isAccredited ?? false,
        rfid: props.user?.rfid ?? '',
        exploitant: props.user?.exploitant?.hasOwnProperty('@id') ? props.user?.exploitant['@id'] : '',
        emailNotificationsLavageImpossible: props.user?.emailNotificationsLavageImpossible ?? false,
        emailNotificationsMaintenance: props.user?.emailNotificationsMaintenance ?? false,
        emailNotificationsLavageDone: props.user?.emailNotificationsLavageDone ?? false,
        RHFeatureEnabled: props.user?.RHFeatureEnabled ?? false,
        isCreating: mode === "create",
    }

    if (isExploitant()) {
        let currentUser = helper.getCurrentUser();
        initialValues.exploitant = currentUser.iri;
    }

    if (props.loading) {
        return <Loader/>;
    }

    return (
        <>
            <PageTitle title={mode === "create" ? "Nouvel Utilisateur" : "Modifier l'utilisateur"} icon={<PeopleAlt/>}/>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={userValidationSchema}
                onSubmit={(values, {setSubmitting}) => {
                    handleSubmit(values)
                    setTimeout(() => {
                        setSubmitting(false)
                    }, 500)
                }}
            >
                {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting,
                      /* and other goodies */
                  }) => (
                    <form onSubmit={handleSubmit}>
                        <UserDataField values={values} mode={mode} exploitants={props.exploitants}
                                       errors={errors} handleChange={handleChange} user={props.user}
                                       handleLogoChanged={handleLogoChanged}
                                       handleBlur={handleBlur}
                                       villes={props.villes}
                                       geoLoading={props.geoLoading}
                                       agences={props.agences}
                        />
                        <Grid container justifyContent="center" style={{marginTop: '2rem'}}>
                            <Grid size={4}>
                                <SubmitButton submitting={isSubmitting}/>
                            </Grid>
                        </Grid>
                    </form>
                )}
            </Formik>
        </>
    )
}

const mapStateToProps = function (state) {
    return {
        user: state.users.user,
        exploitants: state.users.exploitants,
        loading: state.users.loading,
        error: state.users.error,
        villes: state.geo?.villes,
        geoLoading: state.geo?.loading,
        agences: state.agences?.agences,
        agencesLoading: state.agences?.loading,
    }
}

export default connect(mapStateToProps)(UsersNewOrEdit);
